import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardMedia, Grid, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { getCatalogCategories } from '../services/commerce';

function CategoryTile({ category, onSelect }) {
  const name = category?.name ?? 'Category';
  const img = category?.image || noImage;

  return (
    <Card
      elevation={0}
      onClick={() => onSelect(name)}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:active': { bgcolor: 'action.hover' },
        '&:hover': {
          borderColor: { md: 'primary.light' },
          boxShadow: { md: 1 },
        },
      }}
    >
      <CardMedia
        component="img"
        image={img}
        alt={name}
        sx={{
          width: '100%',
          p: { xs: '10px', md: '8px' },
          minHeight: { xs: 132, md: 96 },
          maxHeight: { md: 118 },
          objectFit: 'cover',
          bgcolor: 'grey.100',
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = noImage;
        }}
      />
      <Box
        sx={{
          px: { xs: 1.25, md: 0.75 },
          py: { xs: 1.25, md: 0.75 },
          flex: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          textAlign="center"
          sx={{
            width: '100%',
            lineHeight: 1.25,
            fontSize: { xs: '0.95rem', md: '0.8rem' },
            display: { md: '-webkit-box' },
            WebkitLineClamp: { md: 2 },
            WebkitBoxOrient: { md: 'vertical' },
            overflow: { md: 'hidden' },
          }}
        >
          {name}
        </Typography>
      </Box>
    </Card>
  );
}

function CategoriesSkeleton() {
  return (
    <Grid container spacing={{ xs: 2, md: 1.5 }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              borderRadius: 2,
              minHeight: { xs: 132, md: 118 },
            }}
          />
          <Skeleton width="80%" height={20} sx={{ mx: 'auto', mt: 0.75 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCatalogCategories();
      setCategories(res.data ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const goToCatalog = (categoryName) => {
    navigate(`/v2/catalog?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <PageContainer
      title="Categories"
      sx={{
        maxWidth: { xs: 720, md: 960, lg: 1200 },
        px: { xs: 2, md: 0 },
      }}
    >
      {loading && <CategoriesSkeleton />}

      {!loading && error && (
        <PageState loading={false} error={error} onRetry={load}>
          {null}
        </PageState>
      )}

      {!loading && !error && categories.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No categories available.
        </Typography>
      )}

      {!loading && !error && categories.length > 0 && (
        <Grid container spacing={{ xs: 2, md: 1.5 }}>
          {categories.map((cat) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={cat.documentId || cat.id || cat.name}>
              <CategoryTile category={cat} onSelect={goToCatalog} />
            </Grid>
          ))}
        </Grid>
      )}
    </PageContainer>
  );
}
